import { EventType, PAGE_SIZE, Path } from "constants/constant";
import { Class } from "constants/enum";
import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { contentService } from "services/content.service";
import { Taxon } from "types/types";
import { Analytics } from "utils/analytics";
import { Loader } from "components/Loader";
import { ImageWithPlaceholder } from "components/ImageWithPlaceholder";
import "../App.scss";

type Props = {
  title: string;
  description?: string;
  classes: Class[];
  path: Path;
};

export const TaxonListPage = (props: Props) => {
  const [taxons, setTaxons] = useState<Taxon[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const limit = PAGE_SIZE;
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchTaxons = useCallback(async () => {
    setLoading(true);
    const [newTaxons, totalCount] = await contentService.listTaxons(
      (page - 1) * limit,
      limit,
      props.classes,
    );
    setTaxons((prev) => [...prev, ...newTaxons]);
    setHasMore(page * limit < totalCount);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, props.classes]);

  useEffect(() => {
    fetchTaxons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reset when classes change (navigating between Birds/Butterflies/Fauna)
  useEffect(() => {
    setTaxons([]);
    setPage(1);
    setHasMore(true);
  }, [props.classes]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((p) => p + 1);
          Analytics.track(EventType.PAGE_CHANGED, {
            value: page + 1,
            page: location.pathname,
          });
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, location.pathname]);

  const isLoading = loading && taxons.length === 0;

  return (
    <div id="wrapper" className="fade-in">
      <Container style={{ padding: "2rem 1rem" }}>
        <h1>{props.title}</h1>
        {page === 1 && props.description && <p>{props.description}</p>}
        <Row style={{ padding: "1rem 0" }}>
          {isLoading ? (
            <Loader />
          ) : (
            taxons.map((taxon) => (
              <Col style={{ padding: "1rem 0.5rem" }} md={4} key={taxon.nameEN}>
                <ImageWithPlaceholder
                  src={taxon.images[0]}
                  alt={taxon.nameEN}
                  ratio="3/2"
                />
                <div
                  style={{
                    color: "#242424",
                    padding: "1rem 0.5rem 0rem",
                  }}
                >
                  <h6 style={{ margin: 0 }}>{taxon.nameEN}</h6>
                  <p style={{ margin: "0.25rem 0rem 0rem" }}>{taxon.nameTH}</p>
                  <small style={{ color: "#a7a7a7" }}>
                    <i>{taxon.nameSCI ?? "\u00A0"}</i>
                  </small>
                </div>
              </Col>
            ))
          )}
        </Row>

        {/* Sentinel for infinite scroll */}

        <div ref={sentinelRef} style={{ height: 1 }} />

        {loading && taxons.length > 0 && (
          <div className="d-flex justify-content-center py-4">
            <Loader />
          </div>
        )}

        {!hasMore && taxons.length > 0 && (
          <p
            className="text-center py-3"
            style={{ color: "#a7a7a7", fontSize: "0.85rem" }}
          >
            {taxons.length} species shown
          </p>
        )}
      </Container>
    </div>
  );
};
